import { inject } from '@angular/core';
import { CanDeactivateFn } from '@angular/router';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { TranslocoService } from '@jsverse/transloco';
import { firstValueFrom } from 'rxjs';
import {
  ConfirmData,
  ConfirmDialog,
  openConfirm,
} from '#core/components/confirm-dialog/confirm-dialog.component';

/** A page with a form that is saved with a button, which can say whether it holds changes not saved yet */
export interface HasUnsavedChanges {
  hasUnsavedChanges(): boolean;
}

/** Signed out (by the user, the session running out, or a 401): there is nothing left to save */
const signedIn = (): boolean => !!localStorage.getItem('token');

/** The question asked for a navigation that another one has since replaced */
let asking: MatDialogRef<ConfirmDialog, boolean> | null = null;

/** Asks before leaving a page whose form holds changes that were not saved; leaving is then up to the user */
export const unsavedChangesGuard: CanDeactivateFn<HasUnsavedChanges> = async (page) => {
  asking?.close(false);
  if (!signedIn() || !page?.hasUnsavedChanges()) return true;
  const transloco = inject(TranslocoService);
  const data: ConfirmData = {
    title: transloco.translate('common.unsaved.title'),
    message: transloco.translate('common.unsaved.message'),
    action: transloco.translate('common.unsaved.leave'),
  };
  const ref = openConfirm(inject(MatDialog), data);
  asking = ref;
  const leave = !!(await firstValueFrom(ref.afterClosed()));
  if (asking === ref) asking = null;
  return leave;
};

/**
 * For a page's `window:beforeunload`: closing or reloading the browser tab with changes not
 * saved makes the browser ask first. Not once signed out: the app then leaves for the login itself.
 */
export function askBeforeUnload(page: HasUnsavedChanges, event: BeforeUnloadEvent): void {
  if (signedIn() && page.hasUnsavedChanges()) event.preventDefault();
}
